import type { PersonaOpportunityStatus } from "../api/persona-opportunity-sets.api";
import type { PersonaOpportunityStatusFilter } from "../types/persona-opportunities.types";
import type {
  PersonaOpportunityActiveModel,
  PersonaOpportunityDisplayRow,
  PersonaOpportunityPersonaGroup,
} from "./persona-opportunity-row-model";
import { comparePersonaOpportunityStatus } from "./persona-opportunity-tab-status";

export interface PersonaOpportunityOpportunitiesFilters {
  search: string;
  status: PersonaOpportunityStatusFilter;
}

export const DEFAULT_PERSONA_OPPORTUNITY_OPPORTUNITIES_FILTERS: PersonaOpportunityOpportunitiesFilters =
  {
    search: "",
    status: "all",
  };

export function hasActivePersonaOpportunityOpportunitiesFilters(
  filters: PersonaOpportunityOpportunitiesFilters,
): boolean {
  return filters.search.trim().length > 0 || filters.status !== "all";
}

function matchesSearch(row: PersonaOpportunityDisplayRow, search: string): boolean {
  const needle = search.trim().toLowerCase();
  if (!needle) return true;
  return row.angle.toLowerCase().includes(needle);
}

function matchesStatus(
  row: PersonaOpportunityDisplayRow,
  status: PersonaOpportunityStatusFilter,
): boolean {
  if (status === "all") return true;
  return row.status === (status as PersonaOpportunityStatus);
}

/** Sort opportunities within a persona group: status ordinal, then rank. */
export function sortPersonaOpportunityRows(
  rows: PersonaOpportunityDisplayRow[],
): PersonaOpportunityDisplayRow[] {
  return [...rows].sort((a, b) => {
    const byStatus = comparePersonaOpportunityStatus(a.status, b.status);
    if (byStatus !== 0) return byStatus;
    return a.rank - b.rank;
  });
}

/**
 * Apply Opportunities-tab search + status filter and re-sort rows by status then rank.
 * Empty persona groups are kept when no filters are active (so "no angles found" is visible).
 * With active filters, groups that match nothing are dropped.
 */
export function filterPersonaOpportunityOpportunitiesModel(
  model: PersonaOpportunityActiveModel,
  filters: PersonaOpportunityOpportunitiesFilters,
): PersonaOpportunityActiveModel {
  const filtersActive = hasActivePersonaOpportunityOpportunitiesFilters(filters);
  const groups: PersonaOpportunityPersonaGroup[] = model.groups
    .map((group) => {
      const opportunities = sortPersonaOpportunityRows(
        group.opportunities.filter(
          (row) => matchesSearch(row, filters.search) && matchesStatus(row, filters.status),
        ),
      );
      return { ...group, opportunities };
    })
    .filter((group) => !filtersActive || group.opportunities.length > 0);

  const angleCount = groups.reduce((total, group) => total + group.opportunities.length, 0);

  return {
    groups,
    personaCount: groups.length,
    angleCount,
    competitorsTotal: model.competitorsTotal,
  };
}

/** Flatten filtered (or unfiltered) groups into CSV export rows. */
export function flattenPersonaOpportunityRows(
  groups: PersonaOpportunityPersonaGroup[],
): Array<{ personaTitle: string; row: PersonaOpportunityDisplayRow }> {
  return groups.flatMap((group) =>
    group.opportunities.map((row) => ({ personaTitle: group.title, row })),
  );
}
